/**
 * Wrong-domain investigations: the shape of the investigation JSON stored on
 * `WrongDomainIssue.investigation`, and the rule-based diagnosis that turns the
 * seven checks into causes and recommended steps.
 *
 * Pure and browser-safe: the dashboard renders the same types the background
 * job writes. User-facing copy lives in i18n, keyed by the `key` fields here;
 * nothing in this file is shown to a user verbatim.
 */

// ---------------------------------------------------------------------------
// Checks
// ---------------------------------------------------------------------------

/** The seven checks, in report order (a–g). */
export const WRONG_DOMAIN_CHECK_KEYS = [
	"sightings",
	"address",
	"nameGuess",
	"links",
	"wrongMentions",
	"realMentions",
	"orgMarkup",
] as const;

export type WrongDomainCheckKey = (typeof WRONG_DOMAIN_CHECK_KEYS)[number];

/** The letter each check is labelled with in the report ("check c"). */
export const WRONG_DOMAIN_CHECK_LETTERS: Record<WrongDomainCheckKey, string> = {
	sightings: "a",
	address: "b",
	nameGuess: "c",
	links: "d",
	wrongMentions: "e",
	realMentions: "f",
	orgMarkup: "g",
};

export type WrongDomainCheckStatus = "pending" | "done" | "failed";

export type WrongDomainVerdict = "likely" | "possible" | "ruled_out";

export interface WrongDomainFanoutExample {
	query: string;
	prompt: string;
	platform: string;
	/** ISO date. */
	date: string;
}

/** a. Where and how often AI searched the wrong domain. */
export interface SightingsCheckData {
	totalSearches: number;
	promptRuns: number;
	platforms: string[];
	examples: WrongDomainFanoutExample[];
	/**
	 * Share (0–1) of sightings that came from brand_identity / brand_comparison
	 * prompts. Null when there were no sightings to divide by.
	 */
	brandPromptShare: number | null;
}

export type AddressOutcome =
	| "redirects_to_you"
	| "parked"
	| "other_site"
	| "nothing";

/** b. What answers at the wrong address. */
export interface AddressCheckData {
	outcome: AddressOutcome;
	/** Where the fetch ended up after redirects, when it got anywhere. */
	finalUrl: string | null;
	httpStatus: number | null;
	/** Machine-readable parking/soft-404 signals that drove the outcome. */
	signals: string[];
}

export interface NameGuessAttempt {
	/** Raw model reply, trimmed. */
	reply: string;
	/** Domain parsed from the reply, normalized; null when none was found. */
	domain: string | null;
	/** The parsed domain is the focal domain or one of the additional domains. */
	correct: boolean;
}

export interface NameGuessPlatformResult {
	platform: string;
	model: string | null;
	nameOnly: NameGuessAttempt;
	withDescription: NameGuessAttempt;
}

/** c. Does AI guess the address from the name alone? */
export interface NameGuessCheckData {
	/** The one-line description used for the "name + description" prompt. */
	description: string | null;
	platforms: NameGuessPlatformResult[];
}

export interface LinkExample {
	urlFrom: string;
	domainFrom: string;
	anchor: string | null;
}

/** d. Links pointing at the wrong address. */
export interface LinksCheckData {
	totalBacklinks: number;
	referringDomains: number;
	/** Share (0–1) of sampled links that look automated (directories, etc.). */
	automatedShare: number;
	/** Links from real, editorial pages. */
	editorialCount: number;
	examples: LinkExample[];
}

export interface MentionExample {
	url: string;
	title: string | null;
	snippet: string | null;
	/** ISO date, when known. */
	date: string | null;
}

/** e / f. Pages that write out an address in text. */
export interface MentionsCheckData {
	count: number;
	examples: MentionExample[];
}

/** g. Organization markup on the real homepage. */
export interface OrgMarkupCheckData {
	present: boolean;
	/** The markup's `url` matches the focal domain. */
	urlMatches: boolean;
	sameAsCount: number;
	/** schema.org types found (Organization, NGO, Corporation, …). */
	types: string[];
}

export interface WrongDomainCheckDataMap {
	sightings: SightingsCheckData;
	address: AddressCheckData;
	nameGuess: NameGuessCheckData;
	links: LinksCheckData;
	wrongMentions: MentionsCheckData;
	realMentions: MentionsCheckData;
	orgMarkup: OrgMarkupCheckData;
}

export interface WrongDomainCheck<
	K extends WrongDomainCheckKey = WrongDomainCheckKey,
> {
	key: K;
	status: WrongDomainCheckStatus;
	/** This check's own read, where it implicates a cause. */
	verdict: WrongDomainVerdict | null;
	/** Short machine-written finding (not localized; the UI renders from data). */
	finding: string | null;
	/** What the finding means for the brand (not localized). */
	meaning: string | null;
	data: WrongDomainCheckDataMap[K] | null;
	/** Set when status is "failed". Internal only. */
	error?: string | null;
	/** ISO timestamp the check finished (done or failed). */
	completedAt?: string | null;
}

export type WrongDomainChecks = {
	[K in WrongDomainCheckKey]?: WrongDomainCheck<K>;
};

// ---------------------------------------------------------------------------
// Diagnosis
// ---------------------------------------------------------------------------

export const WRONG_DOMAIN_CAUSE_KEYS = [
	"owned_redirect",
	"other_site",
	"name_guess",
	"rarely_written",
	"links",
	"mentions",
] as const;

export type WrongDomainCauseKey = (typeof WRONG_DOMAIN_CAUSE_KEYS)[number];

/** The check each cause is evidenced by (for "see check c" links). */
export const WRONG_DOMAIN_CAUSE_CHECK: Record<
	WrongDomainCauseKey,
	WrongDomainCheckKey
> = {
	owned_redirect: "address",
	other_site: "address",
	name_guess: "nameGuess",
	rarely_written: "realMentions",
	links: "links",
	mentions: "wrongMentions",
};

export const WRONG_DOMAIN_STEP_KEYS = [
	"claim_domain",
	"write_address",
	"same_website",
	"press",
	"clean_links",
	"correct_mentions",
	"distinguish_name",
	"buy_domain",
] as const;

export type WrongDomainStepKey = (typeof WRONG_DOMAIN_STEP_KEYS)[number];

export interface WrongDomainCause {
	key: WrongDomainCauseKey;
	verdict: WrongDomainVerdict;
}

export interface WrongDomainStep {
	key: WrongDomainStepKey;
	/** The check whose finding motivates the step. */
	basedOn: WrongDomainCheckKey;
}

export interface WrongDomainDiagnosis {
	/** The single most likely cause, or null when nothing is Likely or Possible. */
	primaryCause: WrongDomainCauseKey | null;
	/** Only causes whose evidence is in (their check is done). */
	causes: WrongDomainCause[];
	steps: WrongDomainStep[];
}

/** The full JSON stored on `WrongDomainIssue.investigation`. */
export interface WrongDomainInvestigation {
	version: 1;
	/** ISO timestamp the investigation started. */
	startedAt: string;
	/** ISO timestamp every check reached done or failed. Null while running. */
	completedAt: string | null;
	checks: WrongDomainChecks;
	/** Null until every check has finished. */
	diagnosis: WrongDomainDiagnosis | null;
}

/** At or below this many pages writing out the real address, it's "rarely written". */
export const RARELY_WRITTEN_MAX_MENTIONS = 3;

/** Checks the diagnosis treats as needing a finished result. */
function doneData<K extends WrongDomainCheckKey>(
	checks: WrongDomainChecks,
	key: K,
): WrongDomainCheckDataMap[K] | null {
	const check = checks[key] as WrongDomainCheck<K> | undefined;
	if (!check || check.status !== "done" || !check.data) return null;
	return check.data;
}

const VERDICT_RANK: Record<WrongDomainVerdict, number> = {
	likely: 0,
	possible: 1,
	ruled_out: 2,
};

/**
 * Turn finished checks into causes, a primary cause and recommended steps.
 *
 * Rules (a cause is only listed once its check is done):
 *  - `owned_redirect`: Likely when the wrong address redirects to the brand's
 *    own site (suggests "This domain is ours"). Listed only then.
 *  - `other_site`: Likely when another site lives at the address; otherwise
 *    Ruled out.
 *  - `name_guess`: Likely when some platform guesses wrong from the name alone
 *    and right once given the description; Possible when it guesses wrong from
 *    the name but the description doesn't fix it; Ruled out when every
 *    platform guesses right from the name.
 *  - `rarely_written`: Possible when <= 3 pages write out the real address;
 *    otherwise Ruled out.
 *  - `links`: Ruled out with zero editorial links; Likely with editorial links
 *    when name guessing isn't decisive (not Likely); else Possible.
 *  - `mentions`: Ruled out with zero pages naming the wrong address; Likely
 *    when there are some and name guessing isn't decisive; else Possible.
 *
 * Primary cause = the first Likely cause in priority order (owned_redirect,
 * other_site, name_guess, links, mentions, rarely_written), else the first
 * Possible one, else null.
 */
export function diagnose(checks: WrongDomainChecks): WrongDomainDiagnosis {
	const causes: WrongDomainCause[] = [];

	const address = doneData(checks, "address");
	const nameGuess = doneData(checks, "nameGuess");
	const links = doneData(checks, "links");
	const wrongMentions = doneData(checks, "wrongMentions");
	const realMentions = doneData(checks, "realMentions");
	const orgMarkup = doneData(checks, "orgMarkup");

	if (address?.outcome === "redirects_to_you") {
		causes.push({ key: "owned_redirect", verdict: "likely" });
	}

	if (address) {
		causes.push({
			key: "other_site",
			verdict: address.outcome === "other_site" ? "likely" : "ruled_out",
		});
	}

	let nameGuessVerdict: WrongDomainVerdict | null = null;
	if (nameGuess && nameGuess.platforms.length > 0) {
		const wrongFromName = nameGuess.platforms.filter(
			(p) => !p.nameOnly.correct,
		);
		if (wrongFromName.length === 0) {
			nameGuessVerdict = "ruled_out";
		} else if (wrongFromName.some((p) => p.withDescription.correct)) {
			nameGuessVerdict = "likely";
		} else {
			nameGuessVerdict = "possible";
		}
		causes.push({ key: "name_guess", verdict: nameGuessVerdict });
	}
	const nameGuessDecisive = nameGuessVerdict === "likely";

	const rarelyWritten =
		realMentions !== null &&
		realMentions.count <= RARELY_WRITTEN_MAX_MENTIONS;
	if (realMentions) {
		causes.push({
			key: "rarely_written",
			verdict: rarelyWritten ? "possible" : "ruled_out",
		});
	}

	if (links) {
		causes.push({
			key: "links",
			verdict:
				links.editorialCount === 0
					? "ruled_out"
					: nameGuessDecisive
						? "possible"
						: "likely",
		});
	}

	if (wrongMentions) {
		causes.push({
			key: "mentions",
			verdict:
				wrongMentions.count === 0
					? "ruled_out"
					: nameGuessDecisive
						? "possible"
						: "likely",
		});
	}

	const priority: WrongDomainCauseKey[] = [
		"owned_redirect",
		"other_site",
		"name_guess",
		"links",
		"mentions",
		"rarely_written",
	];
	const byPriority = [...causes].sort(
		(a, b) => priority.indexOf(a.key) - priority.indexOf(b.key),
	);
	const primaryCause =
		byPriority.find((c) => c.verdict === "likely")?.key ??
		byPriority.find((c) => c.verdict === "possible")?.key ??
		null;

	// Report order: strongest verdict first, priority within a verdict.
	const orderedCauses = byPriority.sort(
		(a, b) => VERDICT_RANK[a.verdict] - VERDICT_RANK[b.verdict],
	);

	const verdictOf = (key: WrongDomainCauseKey) =>
		causes.find((c) => c.key === key)?.verdict ?? null;
	const implicated = (key: WrongDomainCauseKey) => {
		const v = verdictOf(key);
		return v === "likely" || v === "possible";
	};

	const steps: WrongDomainStep[] = [];
	if (address?.outcome === "redirects_to_you") {
		steps.push({ key: "claim_domain", basedOn: "address" });
	}
	if (implicated("name_guess")) {
		steps.push({ key: "write_address", basedOn: "nameGuess" });
	}
	if (
		orgMarkup &&
		(!orgMarkup.present ||
			!orgMarkup.urlMatches ||
			orgMarkup.sameAsCount === 0)
	) {
		steps.push({ key: "same_website", basedOn: "orgMarkup" });
	}
	if (rarelyWritten) {
		steps.push({ key: "press", basedOn: "realMentions" });
	}
	if (implicated("links")) {
		steps.push({ key: "clean_links", basedOn: "links" });
	}
	if (implicated("mentions")) {
		steps.push({ key: "correct_mentions", basedOn: "wrongMentions" });
	}
	if (verdictOf("other_site") === "likely") {
		steps.push({ key: "distinguish_name", basedOn: "address" });
	}
	if (address?.outcome === "parked") {
		steps.push({ key: "buy_domain", basedOn: "address" });
	}

	return { primaryCause, causes: orderedCauses, steps };
}

/** True once every check has finished (done or failed). */
export function areChecksComplete(checks: WrongDomainChecks): boolean {
	return WRONG_DOMAIN_CHECK_KEYS.every((key) => {
		const status = checks[key]?.status;
		return status === "done" || status === "failed";
	});
}
